import React, { FC, memo, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
  makeStyles
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import ViewWeekIcon from '@material-ui/icons/ViewWeek';
import { downloadCSV } from 'src/utils';
import { priceColumn } from 'src/utils/fast-moving-products';
import { SelectedColumn } from 'src/types';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import { SoldPerDayCalendar } from './SoldPerDayCalendar';
import { useFastMovingProducts } from 'src/hooks/fast-moving-products';

const useStyles = makeStyles({
  flexBox: {
    display: 'flex',
    alignItems: 'center'
  }
});

interface Props {
  headerKeys?: string[];
  columnIndicesToHide?: number[];
  fastMovingProductToCsv: any[];
  columnToHide: (indices: number[]) => void;
  onApplyFilterColumn: (columnToShow?: SelectedColumn[]) => void;
  // eslint-disable-next-line no-undef
  columnTotal: Record<string, number>;
  categoryId: number;
  queryParams?: {
    [k: string]: string;
  };
}

const { actions: fastMovingActions } = slices.fastMovingProducts;

const component: FC<Props> = ({
  headerKeys,
  fastMovingProductToCsv,
  onApplyFilterColumn,
  columnTotal,
  categoryId,
  queryParams
}) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { getSoldTimelineParams } = useFastMovingProducts();

  const [isColumnFilter, setIsColumnFilter] = useState<boolean>(false);
  const [checkedItems, setCheckedItems] = useState<string[]>([]);
  const [selectedColumn, setSelectedColumn] = useState<SelectedColumn[]>([]);
  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);

  const userBranchOptionsKeys = useMemo(() => {
    const columnObj = headerKeys
      ?.map((keys) => ({
        name: keys,
        value: 0
      }))
      .filter((column) => column?.name !== 'product_name');

    return [...(columnObj || [])];
  }, [headerKeys]);

  const handleToggle = (item: string, selectedColumn: SelectedColumn[]) => {
    setCheckedItems((prev) =>
      prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]
    );

    const isExist = selectedColumn?.find((column) => item === column?.name);

    if (isExist) {
      const filterSelectedColumn = selectedColumn?.filter(
        (column) => column?.name !== item
      );

      setSelectedColumn(filterSelectedColumn);
    } else {
      const findNewColumnVal = userBranchOptionsKeys?.find(
        (branch) => branch?.name === item
      );

      if (findNewColumnVal) {
        setSelectedColumn((prev) => [...prev, findNewColumnVal]);
      }
    }
  };

  const onExportToCSVFastMovingProducts = () => {
    const fastMovingProdCsv = fastMovingProductToCsv?.map((item) => {
      // TypeScript: define newItem as a Record for key-value pairs
      // eslint-disable-next-line no-undef
      const newItem: Record<string, number | string> = {};

      headerKeys?.forEach((key: string) => {
        if (priceColumn.includes(key)) {
          newItem[key] = item[key] ? item[key] : '0.00';
        } else {
          newItem[key] = item[key] ?? 0;
        }

        if (key === 'margin_percentage') {
          newItem[key] = item[key] ? item[key] + '%' : '0%';
        }
      });

      return newItem;
    });

    const keys = selectedColumn?.map((column) => column?.name);

    Object.keys(columnTotal).forEach((key) => {
      columnTotal[key] = 0;
    });

    //transformed data that has computed total sold based on available props
    const csvFastMovingData = fastMovingProdCsv?.map((product) => {
      let total_sold = 0;
      let total_available = 0;

      // First, filter the object based on selected keys
      const filteredEntries = Object.entries(product).filter(
        ([key]) =>
          keys.includes(key) ||
          key === 'product_name' ||
          key === 'total_sold' ||
          key === 'total_available'
      );

      // Then, compute total_sold and total_available from the filtered keys
      const filtered = Object.fromEntries(
        filteredEntries.map(([key, value]) => {
          if (key.includes('sold') && !key.includes('total_sold')) {
            total_sold += Number(value) || 0;
          }
          if (key.includes('available') && !key.includes('total_available')) {
            total_available += Number(value) || 0;
          }
          if (key in columnTotal) {
            columnTotal[key] += Number(value) || 0;
          }
          return [key, value]; // Keep the filtered key-value pairs
        })
      );

      return {
        product_name: product?.product_name, // Always include product_name
        ...filtered,
        total_sold,
        total_available
      };
    });

    if (columnTotal) {
      if (csvFastMovingData?.length) {
        const headerKeys = Object.keys(csvFastMovingData[0]);
        headerKeys.forEach((key) => {
          if (!(key in columnTotal) && key !== 'product_name') {
            columnTotal[key] = 0;
          }
        });

        csvFastMovingData.unshift({
          ...Object.fromEntries(
            headerKeys.map((key) =>
              key === 'product_name'
                ? ['product_name', 'TOTALS']
                : [key, columnTotal[key] ?? 0]
            )
          )
        });
      }
    }

    const csvName = 'FastMovingProduct_PC_Worth.csv';
    downloadCSV(csvFastMovingData, csvName);
  };

  const onHandleApplyFilter = (selectedCol?: SelectedColumn[]) => {
    if (selectedCol) {
      onApplyFilterColumn(selectedCol);

      const safeSelectedCol = selectedCol.map((col) => ({
        name: col.name ?? '',
        value: col.value ?? 0
      }));

      dispatch(fastMovingActions.saveConfigColumn(safeSelectedCol));
      setIsColumnFilter(false);
    }
  };

  const selectedColumnKeys = useAppSelector(
    (state) => state.fastMovingProducts?.configColumn
  );

  const viewSoldPerDay = () => {
    setIsCalendarOpen(true);
    const category_id = categoryId.toString();
    const payload: any = {
      category_id,
      action: 'per_category',
      product_id: undefined
    };

    const preset = queryParams?.preset_dates;
    const dateFrom = queryParams?.date_sold_from;
    const dateTo = queryParams?.date_sold_to;

    if (preset) {
      payload.preset_date = preset;
      payload.from_date = undefined;
      payload.to_date = undefined;
    } else if (dateFrom || dateTo) {
      payload.preset_date = undefined;
      payload.from_date = dateFrom;
      payload.to_date = dateTo;
    }
    getSoldTimelineParams(payload);
  };

  // In the useEffect where we initialize the state:
  useEffect(() => {
    if (selectedColumnKeys && selectedColumnKeys.length > 0) {
      const keys = selectedColumnKeys.map((col) => col.name);
      setCheckedItems(keys);
      setSelectedColumn(selectedColumnKeys);
      onApplyFilterColumn(selectedColumnKeys);
    } else {
      const optionKeys = userBranchOptionsKeys?.map((branch) => branch?.name);
      setCheckedItems(optionKeys);
      setSelectedColumn(userBranchOptionsKeys);
      onApplyFilterColumn(userBranchOptionsKeys);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userBranchOptionsKeys, selectedColumnKeys]);

  return (
    <Paper style={{ marginTop: '1rem' }}>
      <Box
        className={classes.flexBox}
        padding={'1rem'}
        justifyContent={'space-between'}
      >
        <Box className={classes.flexBox} justifyContent={'space-between'}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<GetAppIcon />}
            onClick={onExportToCSVFastMovingProducts}
            style={{ marginRight: '1rem' }}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<CalendarTodayIcon />}
            onClick={() => viewSoldPerDay()}
          >
            Sold per Day
          </Button>
        </Box>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<ViewWeekIcon />}
          onClick={() => setIsColumnFilter(true)}
        >
          Columns Filter
        </Button>
      </Box>

      <SoldPerDayCalendar
        isOpen={isCalendarOpen}
        onHandleClose={() => {
          setIsCalendarOpen(false);
        }}
      />

      <Dialog
        onClose={() => setIsColumnFilter(false)}
        aria-labelledby="simple-dialog-title"
        open={isColumnFilter}
        fullWidth
        maxWidth={'sm'}
      >
        <DialogTitle id="simple-dialog-title">
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h4">Fast Moving Column Filters</Typography>
            <Button
              variant="text"
              color="secondary"
              onClick={() => {
                const allKeys = userBranchOptionsKeys
                  .map((item) => item.name)
                  .filter(Boolean);
                const allSelected = checkedItems.length === allKeys.length;

                if (allSelected) {
                  setCheckedItems([]);
                  setSelectedColumn([]);
                } else {
                  setCheckedItems(allKeys);
                  setSelectedColumn(userBranchOptionsKeys);
                }
              }}
            >
              {checkedItems.length === userBranchOptionsKeys.length
                ? 'Unselect All'
                : 'Select All'}
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ p: 1 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column'
              }}
              style={{ gap: 2 }}
            >
              {userBranchOptionsKeys?.map((item, index) => (
                <Box key={index} display="flex" alignItems="center">
                  <Checkbox
                    checked={checkedItems.includes(item?.name)}
                    onChange={() => handleToggle(item?.name, selectedColumn)}
                  />
                  <Typography variant={'h4'}>{item?.name}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            fullWidth
            onClick={() => onHandleApplyFilter(selectedColumn)}
            variant="contained"
            color="primary"
          >
            Apply Filter
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export const FastMovingProductSubToolbar = memo(component);
